import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '@material-ui/core/Button';
import client from '../Contentful_API/api';
import ArticleLayout, { ArticleFallback } from '../Common/ArticleLayout';
import { LoadingState } from '../Common/PageLayout';
import { assetUrl, formatDate, toExcerpt } from '../Common/useEntries';
import { eventCategory } from './eventFilters';

const SITE = 'https://www.mof.gov.tl';

/** Detail page for a single news item or press release. */
export default function SingleEvent({ match }) {
    const slug = match?.params?.slug;
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);

        client
            .getEntries({ content_type: 'events', 'fields.slug': slug, limit: 1 })
            .then((response) => {
                if (!active) return;
                setEntry(response.items?.[0] || null);
                setLoading(false);
            })
            .catch(() => {
                if (!active) return;
                setEntry(null);
                setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [slug]);

    if (loading) return <LoadingState label="Loading article" />;

    if (!entry) {
        return (
            <ArticleFallback
                title="This news item could not be found"
                description="The article you requested is not available. It may have been moved or withdrawn. Browse all news and press releases to find what you are looking for."
            />
        );
    }

    const fields = entry.fields || {};
    const image = assetUrl(
        fields.eventimage || fields.cover || fields.image || fields.thumbnail
    );
    const published = formatDate(fields.date || entry.sys?.createdAt);
    const body = fields.description || fields.body || '';
    const attachment = assetUrl(fields.attachment || fields.document);

    return (
        <>
            <Helmet defer={false}>
                <title>{`${fields.title} | Ministry of Finance, Timor-Leste`}</title>
                <meta name="description" content={toExcerpt(body, 160)} />
                <meta property="og:title" content={fields.title} />
                <meta property="og:description" content={toExcerpt(body, 160)} />
                {image && <meta property="og:image" content={image} />}
                <meta property="og:type" content="article" />
            </Helmet>

            <ArticleLayout
                eyebrow={eventCategory(entry) || 'News'}
                title={fields.title}
                breadcrumbs={[
                    { label: 'Latest News', to: '/latestnews' },
                    { label: fields.title },
                ]}
                image={image}
                imageAlt={fields.title}
                body={body}
                details={[
                    { label: 'Published', value: published },
                    { label: 'Category', value: eventCategory(entry) },
                ]}
                shareUrl={`${SITE}/eventdetails/${fields.slug}`}
                sidebar={
                    attachment ? (
                        <div style={{ marginBottom: 24 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                href={attachment}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download attachment
                            </Button>
                        </div>
                    ) : null
                }
            />
        </>
    );
}
